/**
 * EncontrosWeekStrip — Missões "Encontros" section
 *
 * Heading + encouragement banner + 7-day strip of meeting check-ins.
 * The strip is rendered through the shared `WeekStripView` so it stays
 * pixel-identical to the sentiment calendar in the Notas tab — same card
 * dimensions, day-number / weekday typography, today-highlight border,
 * and a consistent badge slot. Encontros uses a check-mark badge
 * (Blue Horizon when checked-in, Bege Smoke when not).
 *
 * Encouragement copy is plural-aware: zero / one / many variants render
 * the appropriate i18n string.
 *
 * Spec: Figma 6349:13702 (latest). Section sits on Bege Spring Wood
 * (#F9F8F3); avatar carries the speaker-megaphone glyph on Bege Cararra.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import type { EncontroDay } from '@/services/recoveryPlanService';
import WeekStripView, { type WeekStripDay } from '@/components/WeekStripView';

interface Props {
  days: EncontroDay[];
}

const WEEKDAY_KEYS = [
  'missoes.weekdays.sun',
  'missoes.weekdays.mon',
  'missoes.weekdays.tue',
  'missoes.weekdays.wed',
  'missoes.weekdays.thu',
  'missoes.weekdays.fri',
  'missoes.weekdays.sat',
];

// Figma-exact checkmark — 20x20, strokeWidth 2.
// Bege Smoke (#DAD8CF) when not checked, Blue Horizon (#669AB7) when checked.
const CheckBadge = ({ filled }: { filled: boolean }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M15.8327 5.98975L7.81185 14.0106L4.16602 10.3647"
      stroke={filled ? '#0FA971' : '#DAD8CF'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Speaker-megaphone icon from Figma (16x16).
const MegaphoneIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M12.2808 9.40747C12.2808 9.40747 6.1419 10.3111 5.3713 10.3915C4.6007 10.4719 3.38057 9.8674 2.94662 8.67447C2.51267 7.4816 3.05819 6.23425 3.70036 5.80095C4.34253 5.367 9.60052 2.12891 9.60052 2.12891"
      stroke="#151515"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M5.53125 10.3699L6.51526 13.0735C6.7812 13.8039 7.58813 14.1808 8.31853 13.9142C9.04893 13.6489 9.42513 12.8413 9.1598 12.1116L8.39313 10.0054"
      stroke="#151515"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.7383 5.11026C13.4739 7.1308 13.3344 9.0372 12.4263 9.3674C11.5182 9.6982 10.1858 8.3276 9.45024 6.30703C8.71471 4.28582 8.85484 2.38007 9.76231 2.04925C10.6704 1.71844 12.0027 3.08905 12.7383 5.11026Z"
      stroke="#151515"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Splits a string on <b>…</b> markers and renders the wrapped portion bold.
// Supports zero, one, or many markers (extra-defensive for translations).
function renderWithBold(input: string): React.ReactNode {
  const parts = input.split(/<b>([\s\S]*?)<\/b>/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Text key={i} style={{ fontFamily: 'Inter_700Bold', fontWeight: '700' }}>
        {part}
      </Text>
    ) : (
      part
    ),
  );
}

export default function EncontrosWeekStrip({ days }: Props) {
  const router = useRouter();
  const { t } = useLanguage();
  const todayIso = new Date().toISOString().slice(0, 10);

  const checkedCount = days.filter((d) => d.checked).length;
  const encouragementKey =
    checkedCount === 0
      ? 'missoes.encontros.encouragementZero'
      : checkedCount === 1
        ? 'missoes.encontros.encouragementOne'
        : 'missoes.encontros.encouragement';
  const encouragementText = t(encouragementKey, { count: String(checkedCount) });

  const stripDays: WeekStripDay[] = days.map((d) => ({
    date: d.date,
    dayNumber: String(d.dayNumber),
    dayName: t(WEEKDAY_KEYS[d.weekday] || 'missoes.weekdays.sun'),
    isToday: d.date === todayIso,
    badge: <CheckBadge filled={d.checked} />,
  }));

  const handleDayPress = (_date: string) => {
    router.push('/encontros-online');
  };

  return (
    <View style={styles.wrapper}>
      <View style={styles.encouragementRow}>
        <View style={styles.avatar}>
          <MegaphoneIcon />
        </View>
        <Text style={styles.encouragementText}>
          {renderWithBold(encouragementText)}
        </Text>
      </View>

      <WeekStripView days={stripDays} onDayPress={handleDayPress} />
    </View>
  );
}

const styles = StyleSheet.create({
  // Section: Bege Spring Wood, padding 24 / 24 / 24 / 24, gap 12
  wrapper: {
    backgroundColor: '#F9F8F3',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    gap: 12,
    width: '100%',
  },
  heading: {
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 19.6,
  },
  encouragementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    width: '100%',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F0EEE4',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  encouragementText: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
});
